// F7: tabela com paginação por cursor, busca, ordenação e visão salva (N9); estados vazio, carregando e erro;
// no mobile vira lista de cartões (sem scroll horizontal).
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowDownUp, Bookmark, Columns3, Search } from 'lucide-react';
import { api } from '../api';
import { Carregando, Erro, Vazio } from './Estados';
import { Modal } from './Sobreposicoes';

export interface Coluna<T> {
  chave: string;
  rotulo: string;
  render: (linha: T) => ReactNode;
  /** coluna some abaixo de md (no cartão mobile aparece só se `noCartao`) */
  secundaria?: boolean;
  noCartao?: boolean;
  alinhar?: 'direita';
}

interface Visao {
  id: string;
  nome: string;
  filtros: Record<string, string>;
  ordenacao: { campo: string; dir: 'asc' | 'desc' }[];
  /** SB-03: chaves das colunas visíveis (vazio = todas) */
  colunas?: string[];
  padrao: boolean;
}

export interface PropsTabela<T> {
  /** identifica a tela nas visões salvas, ex.: 'cadastros.clientes' */
  tela: string;
  chaveConsulta: unknown[];
  /** monta a URL a partir de filtros e cursor */
  url: (p: { busca: string; ordenar: string; cursor?: string | undefined; filtros: Record<string, string> }) => string;
  colunas: Coluna<T>[];
  idDe: (linha: T) => string;
  aoAbrir?: (linha: T) => void;
  ordens?: { valor: string; rotulo: string }[];
  filtrosExtras?: (f: Record<string, string>, set: (f: Record<string, string>) => void) => ReactNode;
  vazio: { titulo: string; texto?: string; acao?: ReactNode };
  /** entidade dos campos personalizados (N8), ex.: 'cadastros.parceiro'; as linhas trazem `extras` */
  entidade?: string;
}

interface CampoPersonalizado {
  chave: string;
  rotulo: string;
  tipo: 'texto' | 'numero' | 'data' | 'lista' | 'booleano';
}

/** N8: valor de campo personalizado formatado pelo tipo e pelo idioma ativo. */
function formatarExtra(v: unknown, tipo: CampoPersonalizado['tipo'], idioma: string, sim: string, nao: string): string {
  if (v === undefined || v === null || v === '') return '';
  if (tipo === 'numero' && typeof v === 'number') return v.toLocaleString(idioma);
  if (tipo === 'data' && typeof v === 'string') return new Date(`${v}T12:00:00`).toLocaleDateString(idioma);
  if (tipo === 'booleano') return v ? sim : nao;
  return String(v);
}

export function Tabela<T>({ tela, chaveConsulta, url, colunas: base, idDe, aoAbrir, ordens, filtrosExtras, vazio, entidade }: PropsTabela<T>) {
  const { t, i18n } = useTranslation();
  // N8: cada campo personalizado da entidade vira coluna secundária (aparece no desktop largo e não no cartão).
  const campos = useQuery({
    queryKey: ['nucleo', 'campos', entidade],
    queryFn: () => api<CampoPersonalizado[]>(`/api/v1/nucleo/campos?entidade=${encodeURIComponent(entidade!)}`),
    enabled: !!entidade,
    staleTime: 60_000,
  });
  const colunas = useMemo<Coluna<T>[]>(
    () => [
      ...base,
      ...(campos.data ?? []).map((c) => ({
        chave: `extras.${c.chave}`,
        rotulo: c.rotulo,
        secundaria: true,
        render: (l: T) => formatarExtra((l as { extras?: Record<string, unknown> }).extras?.[c.chave], c.tipo, i18n.language, t('tabela.sim'), t('tabela.nao')),
      })),
    ],
    [base, campos.data, i18n.language, t],
  );
  const qc = useQueryClient();
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [ordenar, setOrdenar] = useState(ordens?.[0]?.valor ?? '');
  const [filtros, setFiltros] = useState<Record<string, string>>({});
  // SB-03 (N9): colunas visíveis escolhidas pelo usuário; a primeira coluna nunca some.
  const [ocultas, setOcultas] = useState<Set<string>>(new Set());
  const [escolhendoColunas, setEscolhendoColunas] = useState(false);
  const [salvando, setSalvando] = useState<{ nome: string; padrao: boolean } | null>(null);

  useEffect(() => {
    const h = setTimeout(() => setBuscaAplicada(busca.trim()), 300);
    return () => clearTimeout(h);
  }, [busca]);

  const visoes = useQuery({ queryKey: ['nucleo', 'visoes', tela], queryFn: () => api<Visao[]>(`/api/v1/nucleo/visoes?tela=${encodeURIComponent(tela)}`) });
  const [visaoAplicada, setVisaoAplicada] = useState<string | null>(null);
  useEffect(() => {
    const padrao = visoes.data?.find((v) => v.padrao);
    if (padrao && visaoAplicada === null) aplicar(padrao);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visoes.data]);
  function aplicar(v: Visao) {
    setVisaoAplicada(v.id);
    setBusca(v.filtros.busca ?? '');
    setBuscaAplicada(v.filtros.busca ?? '');
    const { busca: _b, ...resto } = v.filtros;
    setFiltros(resto);
    if (v.ordenacao[0]) setOrdenar(v.ordenacao[0].campo);
    setOcultas(v.colunas?.length ? new Set(colunas.slice(1).map((c) => c.chave).filter((k) => !v.colunas!.includes(k))) : new Set());
  }
  const visiveis = useMemo(() => colunas.filter((c, i) => i === 0 || !ocultas.has(c.chave)), [colunas, ocultas]);
  const salvar = useMutation({
    // A direção da ordem vai codificada no próprio valor da ordem (cada módulo define as suas, ex.: '-criado').
    mutationFn: (v: { nome: string; padrao: boolean }) => api<{ id: string }>('/api/v1/nucleo/visoes', { method: 'POST', json: { tela, nome: v.nome, filtros: { ...filtros, ...(buscaAplicada ? { busca: buscaAplicada } : {}) }, colunas: visiveis.map((c) => c.chave), ordenacao: ordenar ? [{ campo: ordenar, dir: 'asc' }] : [], padrao: v.padrao } }),
    onSuccess: (r) => {
      setSalvando(null);
      setVisaoAplicada(r.id);
      void qc.invalidateQueries({ queryKey: ['nucleo', 'visoes', tela] });
    },
  });

  const q = useInfiniteQuery({
    queryKey: [...chaveConsulta, { buscaAplicada, ordenar, filtros }],
    queryFn: ({ pageParam }) => api<{ itens: T[]; proximo: string | null }>(url({ busca: buscaAplicada, ordenar, cursor: pageParam, filtros })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (u) => u.proximo ?? undefined,
  });
  const linhas = useMemo(() => q.data?.pages.flatMap((p) => p.itens) ?? [], [q.data]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <label className="relative w-full min-w-0 sm:w-auto sm:min-w-56 sm:max-w-sm sm:flex-1">
          <span className="sr-only">{t('tabela.buscar')}</span>
          <Search size={16} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-tinta-3" />
          <input value={busca} onChange={(e) => setBusca(e.target.value)} maxLength={80} placeholder={t('tabela.buscar')} className="h-10 w-full rounded-lg border border-borda bg-sup-1 pr-3 pl-9 text-sm outline-none placeholder:text-tinta-3 focus:border-acento" />
        </label>
        {filtrosExtras?.(filtros, setFiltros)}
        {ordens && ordens.length > 1 && (
          <label className="flex items-center gap-2 text-sm text-tinta-3">
            <ArrowDownUp size={15} />
            <span className="sr-only">{t('tabela.ordenar')}</span>
            <select value={ordenar} onChange={(e) => setOrdenar(e.target.value)} className="h-10 rounded-lg border border-borda bg-sup-1 px-2 text-sm text-tinta-1 outline-none focus:border-acento">
              {ordens.map((o) => (
                <option key={o.valor} value={o.valor}>
                  {o.rotulo}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="flex items-center gap-1">
          <Bookmark size={15} className="text-tinta-3" />
          <select
            aria-label={t('tabela.visoes')}
            value={visaoAplicada ?? ''}
            onChange={(e) => {
              const v = visoes.data?.find((x) => x.id === e.target.value);
              if (v) aplicar(v);
              else {
                setVisaoAplicada('');
                setFiltros({});
                setBusca('');
              }
            }}
            className="h-10 max-w-40 rounded-lg border border-borda bg-sup-1 px-2 text-sm text-tinta-1 outline-none focus:border-acento"
          >
            <option value="">{t('tabela.semVisao')}</option>
            {visoes.data?.map((v) => (
              <option key={v.id} value={v.id}>
                {v.nome}
              </option>
            ))}
          </select>
          <button type="button" onClick={() => setSalvando({ nome: '', padrao: false })} className="h-10 rounded-lg px-2 text-sm text-tinta-3 hover:bg-sup-2 hover:text-tinta-1">
            {t('tabela.salvarVisao')}
          </button>
        </div>
        {colunas.length > 2 && (
          <div className="relative hidden md:block">
            <button type="button" onClick={() => setEscolhendoColunas((x) => !x)} aria-expanded={escolhendoColunas} className="flex h-10 items-center gap-1.5 rounded-lg px-2 text-sm text-tinta-3 hover:bg-sup-2 hover:text-tinta-1">
              <Columns3 size={15} /> {t('tabela.colunas')}
            </button>
            {escolhendoColunas && (
              <div role="group" aria-label={t('tabela.colunas')} className="absolute right-0 z-20 mt-1 flex w-56 flex-col gap-1 rounded-lg border border-borda bg-sup-1 p-2 shadow-xl">
                {colunas.slice(1).map((c) => (
                  <label key={c.chave} className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-sup-2">
                    <input
                      type="checkbox"
                      checked={!ocultas.has(c.chave)}
                      onChange={(e) => setOcultas((o) => { const n = new Set(o); if (e.target.checked) n.delete(c.chave); else n.add(c.chave); return n; })}
                      className="accent-acento"
                    />
                    {c.rotulo}
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        aberto={!!salvando}
        aoFechar={() => setSalvando(null)}
        titulo={t('tabela.salvarVisao')}
        rodape={
          <>
            <button type="button" onClick={() => setSalvando(null)} className="h-10 rounded-lg border border-borda px-4 text-sm hover:bg-sup-2">{t('acao.cancelar')}</button>
            <button type="button" disabled={!salvando?.nome.trim() || salvar.isPending} onClick={() => salvando && salvar.mutate({ nome: salvando.nome.trim(), padrao: salvando.padrao })} className="h-10 rounded-lg bg-acento px-4 text-sm font-medium text-acento-tinta hover:bg-acento-forte disabled:opacity-50">{t('acao.salvar')}</button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5 text-sm text-tinta-2">
            {t('tabela.nomeVisao')}
            <input value={salvando?.nome ?? ''} onChange={(e) => setSalvando((v) => v && { ...v, nome: e.target.value })} maxLength={80} className="h-10 rounded-lg border border-borda bg-sup-2 px-3 text-tinta-1 outline-none focus:border-acento" />
          </label>
          <label className="flex items-center gap-2 text-sm text-tinta-2">
            <input type="checkbox" checked={salvando?.padrao ?? false} onChange={(e) => setSalvando((v) => v && { ...v, padrao: e.target.checked })} className="accent-acento" />
            {t('tabela.usarComoPadrao')}
          </label>
          <p className="text-xs text-tinta-3">{t('tabela.salvaInclui')}</p>
        </div>
      </Modal>

      {q.isPending ? (
        <Carregando />
      ) : q.isError ? (
        <Erro aoTentar={() => void q.refetch()} />
      ) : linhas.length === 0 && (buscaAplicada || Object.values(filtros).some(Boolean)) ? (
        <Vazio titulo={t('tabela.semResultado')} texto={t('tabela.semResultadoTexto')} acao={<button type="button" onClick={() => { setBusca(''); setBuscaAplicada(''); setFiltros({}); setVisaoAplicada(''); }} className="h-9 rounded-lg border border-borda px-4 text-sm hover:bg-sup-2">{t('tabela.limparBusca')}</button>} />
      ) : linhas.length === 0 ? (
        <Vazio titulo={vazio.titulo} texto={vazio.texto} acao={vazio.acao} />
      ) : (
        <>
          <div className="hidden overflow-x-auto rounded-xl border border-borda md:block">
            <table className="w-full text-left text-sm">
              <thead className="bg-sup-1 text-tinta-3">
                <tr>
                  {visiveis.map((c) => (
                    <th key={c.chave} scope="col" className={`px-4 py-3 font-medium ${c.alinhar === 'direita' ? 'text-right' : ''} ${c.secundaria ? 'hidden lg:table-cell' : ''}`}>
                      {c.rotulo}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-borda">
                {linhas.map((l) => (
                  // VB14-01 (rodada 14): a linha que abre a ficha entra no Tab e abre com Enter ou Espaço.
                  <tr
                    key={idDe(l)}
                    onClick={() => aoAbrir?.(l)}
                    tabIndex={aoAbrir ? 0 : undefined}
                    onKeyDown={aoAbrir ? (ev) => { const tecla = ev.key; if ((tecla === 'Enter' || tecla === ' ') && ev.target === ev.currentTarget) { ev.preventDefault(); aoAbrir(l); } } : undefined}
                    className={aoAbrir ? 'cursor-pointer hover:bg-sup-1 focus-visible:bg-sup-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-acento' : ''}
                  >
                    {visiveis.map((c) => (
                      <td key={c.chave} className={`px-4 py-3 align-middle ${c.alinhar === 'direita' ? 'text-right' : ''} ${c.secundaria ? 'hidden lg:table-cell' : ''}`}>
                        {c.render(l)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ul className="flex flex-col gap-2 md:hidden">
            {linhas.map((l) => (
              <li key={idDe(l)}>
                <button type="button" onClick={() => aoAbrir?.(l)} className="flex w-full flex-col gap-1 rounded-xl border border-borda bg-sup-1 p-4 text-left">
                  <span className="min-w-0 font-medium">{colunas[0]?.render(l)}</span>
                  {colunas.slice(1).filter((c) => c.noCartao).map((c) => (
                    // VB6-03 (rodada 6): no cartão o valor vem com o rótulo da coluna (antes um número solto).
                    <span key={c.chave} className="flex min-w-0 gap-2 text-sm">
                      <span className="shrink-0 text-tinta-3">{c.rotulo}</span>
                      <span className="min-w-0 text-tinta-2">{c.render(l)}</span>
                    </span>
                  ))}
                </button>
              </li>
            ))}
          </ul>
          {q.hasNextPage && (
            <button type="button" onClick={() => void q.fetchNextPage()} disabled={q.isFetchingNextPage} className="self-center rounded-lg border border-borda px-4 py-2 text-sm hover:bg-sup-2 disabled:opacity-50">
              {q.isFetchingNextPage ? t('estado.carregando') : t('tabela.carregarMais')}
            </button>
          )}
        </>
      )}
    </div>
  );
}

/** Chip de tag com cor por token (cadastros_tags.cor). */
const COR_CHIP: Record<string, string> = {
  acento: 'bg-acento-suave text-tinta-1',
  roxo: 'bg-roxo/15 text-roxo',
  azul: 'bg-azul/15 text-azul',
  ciano: 'bg-ciano/15 text-ciano',
  laranja: 'bg-laranja/15 text-laranja',
  dourado: 'bg-dourado/15 text-dourado',
  sucesso: 'bg-sucesso/15 text-sucesso',
};
export function Chip({ texto, cor = 'acento' }: { texto: string; cor?: string }) {
  return <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${COR_CHIP[cor] ?? COR_CHIP.acento}`}>{texto}</span>;
}
